import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  applyLessonRename,
  applyLessonRenames,
  applyModuleRename,
  applyModuleRenames,
  listLessonRenames,
  listModuleRenames,
  updateLessonRename,
  updateModuleRename,
} from '@platform/client';
import {
  lessonRenameListSchema,
  moduleRenameListSchema,
} from '@platform/schema/curriculum';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { counted } from '../library/counted';

import type { UpdateRenameDto as UpdateRename } from '@platform/client';
import type { LessonRename, ModuleRename } from '@platform/schema/curriculum';

type Review = ModuleRename | LessonRename;

interface Kind<T extends Review> {
  key: string;
  title: string;
  intro: string;
  noun: string;
  linked: string;
  list: () => Promise<{ renames: T[]; pending: number }>;
  update: (id: string, body: UpdateRename) => Promise<unknown>;
  approveOne: (id: string) => Promise<{ renamed: number }>;
  approveAll: () => Promise<{ renamed: number }>;
  /** ID, course and where else the name is used. */
  identity: (row: T) => ReactNode;
  /** What opens under the row: a module's lessons, a lesson's description. */
  details: { label: (row: T) => string; body: (row: T) => ReactNode };
}

const MODULES: Kind<ModuleRename> = {
  key: 'module-renames',
  title: 'Review duplicate module names',
  intro:
    'These are separate modules in different courses that share a name. Approving renames one module; its ID, lessons, order and links stay. Nothing is merged or deleted, and a shared name never blocks importing or building.',
  noun: 'module',
  linked: 'the app module',
  list: async () =>
    moduleRenameListSchema.parse(
      (await listModuleRenames({ throwOnError: true })).data,
    ),
  update: (id, body) =>
    updateModuleRename({ path: { id }, body, throwOnError: true }),
  approveOne: async (id) =>
    (await applyModuleRename({ path: { id }, throwOnError: true })).data,
  approveAll: async () =>
    (await applyModuleRenames({ throwOnError: true })).data,
  identity: (row) => (
    <>
      <Typography variant="body2" color="text.secondary">
        {row.course} · ID <code>{row.moduleId ?? 'not in the plan'}</code>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Also named “{row.module}”:{' '}
        {row.matches
          .map(
            (match) =>
              `${match.course}${match.currentName !== null && match.currentName !== row.module ? ` (now “${match.currentName}”)` : ''}`,
          )
          .join(', ')}
      </Typography>
    </>
  ),
  details: {
    label: (row) => counted(row.lessons.length, 'lesson'),
    body: (row) => (
      <Box component="ol" sx={{ m: 0, pl: 3 }}>
        {row.lessons.map((lesson, index) => (
          <li key={`${String(index)}-${lesson}`}>
            <Typography variant="body2">{lesson}</Typography>
          </li>
        ))}
      </Box>
    ),
  },
};

const LESSONS: Kind<LessonRename> = {
  key: 'lesson-renames',
  title: 'Review duplicate lesson titles',
  intro:
    'These are separate lessons that share a title. Each proposal was written from the lesson’s description and writer brief. Approving retitles one planned lesson; its ID, module, order and sources stay, and a later import keeps the new title.',
  noun: 'lesson',
  linked: 'the app lesson',
  list: async () =>
    lessonRenameListSchema.parse(
      (await listLessonRenames({ throwOnError: true })).data,
    ),
  update: (id, body) =>
    updateLessonRename({ path: { id }, body, throwOnError: true }),
  approveOne: async (id) =>
    (await applyLessonRename({ path: { id }, throwOnError: true })).data,
  approveAll: async () =>
    (await applyLessonRenames({ throwOnError: true })).data,
  identity: (row) => (
    <>
      <Typography variant="body2" color="text.secondary">
        <code>{row.lessonId}</code> · {row.course ?? 'not in the plan'}
        {row.module !== null && ` › ${row.module}`}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Also titled “{row.name}”:{' '}
        {row.matches
          .map(
            (match) =>
              `${match.lessonId} (${match.course ?? '?'} › ${match.module ?? '?'})`,
          )
          .join(', ')}
      </Typography>
    </>
  ),
  details: {
    label: () => 'description',
    body: (row) => <Typography variant="body2">{row.description}</Typography>,
  },
};

export function ModuleRenameDialog(props: {
  open: boolean;
  onClose: () => void;
}) {
  return <ReviewDialog kind={MODULES} {...props} />;
}

export function LessonRenameDialog(props: {
  open: boolean;
  onClose: () => void;
}) {
  return <ReviewDialog kind={LESSONS} {...props} />;
}

/** The duplicate-name reviews — CURR-17's module renames, made reviewable by CURR-32. */
function ReviewDialog<T extends Review>({
  kind,
  open,
  onClose,
}: {
  kind: Kind<T>;
  open: boolean;
  onClose: () => void;
}) {
  const queries = useQueryClient();
  const queryKey = ['curriculum', kind.key];
  const rows = useQuery({ queryKey, enabled: open, queryFn: kind.list });
  const refresh = () => queries.invalidateQueries({ queryKey });

  const update = useTrackedMutation(`Review a proposed ${kind.noun} name`, {
    mutationFn: (change: { id: string; body: UpdateRename }) =>
      kind.update(change.id, change.body),
    onSuccess: refresh,
  });
  const approveOne = useTrackedMutation(`Approve a ${kind.noun} rename`, {
    mutationFn: async (change: { id: string; proposedName?: string }) => {
      if (change.proposedName !== undefined)
        await kind.update(change.id, { proposedName: change.proposedName });
      return kind.approveOne(change.id);
    },
    onSuccess: () => queries.invalidateQueries({ queryKey: ['curriculum'] }),
  });
  const approveAll = useTrackedMutation(`Approve the ${kind.noun} renames`, {
    mutationFn: kind.approveAll,
    onSuccess: () => queries.invalidateQueries({ queryKey: ['curriculum'] }),
  });

  const close = () => {
    approveOne.reset();
    approveAll.reset();
    onClose();
  };
  const error =
    rows.error ?? update.error ?? approveOne.error ?? approveAll.error;
  const renamed = approveAll.data?.renamed ?? approveOne.data?.renamed;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle>{kind.title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {kind.intro}
        </Typography>
        {error !== null && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {describeApiError(error)}
          </Alert>
        )}
        {renamed !== undefined && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Renamed {counted(renamed, kind.noun)}.
          </Alert>
        )}
        <Stack spacing={1.5}>
          {(rows.data?.renames ?? []).map((row) => (
            <ReviewRow
              key={row.id}
              kind={kind}
              row={row}
              busy={approveOne.isPending || approveAll.isPending}
              onUpdate={(body) => {
                update.mutate({ id: row.id, body });
              }}
              onApprove={(proposedName) => {
                approveOne.mutate({
                  id: row.id,
                  ...(proposedName === row.proposedName
                    ? {}
                    : { proposedName }),
                });
              }}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
        <Button
          variant="contained"
          disabled={(rows.data?.pending ?? 0) === 0 || approveAll.isPending}
          onClick={() => {
            approveAll.mutate();
          }}
        >
          Approve all {counted(rows.data?.pending ?? 0, 'remaining rename')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReviewRow<T extends Review>({
  kind,
  row,
  busy,
  onUpdate,
  onApprove,
}: {
  kind: Kind<T>;
  row: T;
  busy: boolean;
  onUpdate: (body: UpdateRename) => void;
  onApprove: (proposedName: string) => void;
}) {
  const [draft, setDraft] = useState(row.proposedName);
  const [expanded, setExpanded] = useState(false);
  const name = row.currentName ?? 'Not in the plan';
  const linked = kind.linked;

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={`${name} — ${kind.noun}`}
      sx={{ p: 1.5, opacity: row.skipped ? 0.6 : 1 }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle2">{name}</Typography>
        {row.applied && <Chip size="small" color="success" label="Approved" />}
        {row.skipped && <Chip size="small" label="Skipped" />}
      </Stack>
      {kind.identity(row)}

      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1, alignItems: 'flex-start' }}
      >
        <TextField
          size="small"
          label="Proposed name"
          value={draft}
          disabled={row.applied}
          sx={{ flex: 1 }}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onBlur={() => {
            const next = draft.trim();
            if (next !== '' && next !== row.proposedName)
              onUpdate({ proposedName: next });
          }}
        />
        <Button
          variant="outlined"
          disabled={row.applied || busy || row.currentName === null}
          onClick={() => {
            onApprove(draft.trim() === '' ? row.proposedName : draft.trim());
          }}
        >
          Approve
        </Button>
        <Button
          disabled={row.applied}
          onClick={() => {
            onUpdate({ skipped: !row.skipped });
          }}
        >
          {row.skipped ? 'Undo skip' : 'Skip'}
        </Button>
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Why: {row.reason}
      </Typography>

      {row.linked === null ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Changes planning data only: no app {kind.noun} is linked.
        </Typography>
      ) : (
        <>
          <FormControlLabel
            disabled={row.applied}
            control={
              <Checkbox
                size="small"
                checked={row.renameLinked}
                onChange={(event) => {
                  onUpdate({ renameLinked: event.target.checked });
                }}
              />
            }
            label={`Also rename ${linked} “${row.linked.title}” (${row.linked.status.toLowerCase()})`}
          />
          <Typography variant="body2" color="text.secondary">
            {row.touches === 'both'
              ? `Changes planning data and ${linked}. Reverting from the history restores the planned name only.`
              : `Changes planning data only: ${linked} keeps its name.`}
          </Typography>
        </>
      )}

      <Button
        size="small"
        sx={{ mt: 0.5 }}
        aria-expanded={expanded}
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        {expanded ? 'Hide' : 'Show'} {kind.details.label(row)}
      </Button>
      <Collapse in={expanded} unmountOnExit>
        {kind.details.body(row)}
      </Collapse>
    </Paper>
  );
}
